/**
 * Brand mark for a property. `brand` is the free-text `brand` column of
 * property_directory; the five brands PazNetworks manages today have an asset
 * in /public/brands, anything else renders as text so a new brand never
 * shows a broken image.
 *
 * `kind` decides how the asset sits in the badge: a `wordmark` is a wide,
 * short lockup and reads at 20px tall; a `mark` is a near-square glyph (the
 * DoubleTree tree-in-a-D) and needs the full badge height to stay legible.
 */
type BrandAsset = { src: string; kind: 'wordmark' | 'mark' };

const KNOWN_BRAND_LOGOS: Record<string, BrandAsset> = {
  hilton: { src: '/brands/hilton.svg', kind: 'wordmark' },
  // Icon only, per owner request 2026-09-21: the tree-D glyph cropped from
  // doubletree-mark.png, with the "DoubleTree by Hilton" lines removed.
  doubletree: { src: '/brands/doubletree-icon.png', kind: 'mark' },
  marriott: { src: '/brands/marriott.svg', kind: 'wordmark' },
  sheraton: { src: '/brands/sheraton.svg', kind: 'wordmark' },
  luma: { src: '/brands/luma.svg', kind: 'wordmark' },
};

export function BrandLogo({
  brand,
  name,
  className = '',
}: {
  brand: string | null | undefined;
  name: string;
  className?: string;
}) {
  const key = brand?.trim().toLowerCase();
  if (!key) return null;
  const asset = KNOWN_BRAND_LOGOS[key];
  const isMark = asset?.kind === 'mark';

  return (
    <div
      className={`inline-flex h-9 items-center justify-center overflow-hidden rounded-sm bg-white ${isMark ? 'px-2' : 'px-3'} ${className}`}
      style={{ border: '1px solid var(--border)' }}
    >
      {asset && (
        <img
          src={asset.src}
          alt={`${name} logo`}
          className={`${isMark ? 'h-[80%]' : 'h-5'} w-auto max-w-[140px] object-contain`}
          onError={(event) => {
            const el = event.currentTarget;
            el.style.display = 'none';
            const fallback = el.nextElementSibling;
            if (fallback instanceof HTMLElement) fallback.hidden = false;
          }}
        />
      )}
      <span
        hidden={!!asset}
        className="text-[11px] font-semibold uppercase tracking-wide"
        style={{ color: '#1B365D' }}
      >
        {brand}
      </span>
    </div>
  );
}
